import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, ArrowLeft, Loader2, Sparkles, Heart, Brain } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 🧠 Allora Chat - Personal AI Assistant with Emotional Intelligence
 * 
 * This is the dedicated Allora experience with:
 * - Full emotional intelligence engine
 * - Memory and context awareness
 * - Personalized responses based on user history
 * - Activity-aware suggestions
 */
const AlloraChat = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, locale } = useLocale();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [emotionalContext, setEmotionalContext] = useState(null);
  const messagesEndRef = useRef(null);

  // Load Allora's context and chat history
  useEffect(() => {
    const loadAlloraContext = async () => {
      try {
        // Get emotional context for personalized greeting
        const contextResponse = await fetch(`${API_URL}/api/adaptive/activity-context`, {
          credentials: 'include'
        });
        
        if (contextResponse.ok) {
          const context = await contextResponse.json();
          setEmotionalContext(context);
        }

        // Load Allora-specific chat history
        const historyResponse = await fetch(`${API_URL}/api/chat/messages?session_id=allora`, {
          credentials: 'include'
        });
        
        if (historyResponse.ok) {
          const history = await historyResponse.json();
          
          if (history.length === 0) {
            // Generate personalized welcome based on context
            const welcomeMessage = generateWelcomeMessage(emotionalContext);
            setMessages([{
              id: 'welcome',
              role: 'assistant',
              content: welcomeMessage,
              timestamp: new Date().toISOString(),
              isAllora: true
            }]);
          } else {
            setMessages(history.map(msg => ({ ...msg, isAllora: true })));
          }
        }
      } catch (error) {
        console.error('Error loading Allora context:', error);
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: t('allora.welcome') || "Hey! I'm Allora. I remember how yesterday felt and I'm here to help you today. How are you feeling?",
          timestamp: new Date().toISOString(),
          isAllora: true
        }]);
      } finally {
        setInitialLoading(false);
      }
    };

    loadAlloraContext();
  }, [t]);

  // Generate personalized welcome based on user context
  const generateWelcomeMessage = (context) => {
    const hour = new Date().getHours();
    let greeting = '';
    
    if (hour >= 5 && hour < 12) {
      greeting = t('allora.goodMorning') || 'Good morning';
    } else if (hour >= 12 && hour < 17) {
      greeting = t('allora.goodAfternoon') || 'Good afternoon';
    } else if (hour >= 17 && hour < 21) {
      greeting = t('allora.goodEvening') || 'Good evening';
    } else {
      greeting = t('allora.lateNight') || "It's late";
    }

    const name = user?.name?.split(' ')[0] || '';
    return `${greeting}${name ? ', ' + name : ''}! ${t('allora.imHereForYou') || "I'm Allora. I remember how you've been feeling and I'm here to help. What's on your mind?"} 💜`;
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input.trim();
    setInput('');
    setLoading(true);

    try {
      // Use Allora-specific chat endpoint with emotional context
      const response = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userInput,
          session_id: 'allora',
          language: locale,
          use_emotional_context: true,  // Enable EI engine
          use_memory: true              // Enable memory system
        })
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage = {
          id: `msg-${Date.now()}-ai`,
          role: 'assistant',
          content: data.response || data.message,
          timestamp: new Date().toISOString(),
          isAllora: true,
          emotionalState: data.emotional_state
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: t('error.title') || 'Error',
        description: t('error.tryAgain') || 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center animate-pulse">
            <Heart className="h-8 w-8 text-white" />
          </div>
          <p className="text-purple-600 font-medium">{t('allora.loading') || 'Allora is preparing...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-white flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-purple-100 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-purple-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-purple-600" />
          </button>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-lg">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="font-semibold text-purple-900">{t('allora.name') || 'Allora'}</h1>
            <p className="text-xs text-purple-500">{t('allora.subtitle') || 'Your personal AI companion'}</p>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 rounded-full">
            <Brain className="h-3 w-3 text-purple-600" />
            <span className="text-xs text-purple-600 font-medium">{t('allora.emotionalAI') || 'EI Active'}</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-md'
                    : 'bg-white shadow-soft border border-purple-100 text-gray-800 rounded-bl-md'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-purple-500 text-xs font-medium">💜 Allora</span>
                  </div>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white shadow-soft border border-purple-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                  <span className="text-sm text-purple-500">{t('allora.thinking') || 'Allora is thinking...'}</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="sticky bottom-0 bg-white/80 backdrop-blur-lg border-t border-purple-100 px-4 py-4">
        <form onSubmit={handleSendMessage} className="max-w-2xl mx-auto">
          <div className="flex gap-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('allora.placeholder') || "Share what's on your mind..."}
              className="flex-1 bg-white border-purple-200 focus:border-purple-400 focus:ring-purple-400 rounded-xl"
              disabled={loading}
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl px-4"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
          <p className="text-xs text-center text-purple-400 mt-2">
            {t('allora.privacyNote') || 'Allora remembers our conversations to help you better 💜'}
          </p>
        </form>
      </div>
    </div>
  );
};

export default AlloraChat;
